import Axios from '../../utils/axios';
import { getBackendErrorMessage } from '../../utils/errorHandler';

/**
 * Talent classes in nest-driver-service (`m_driver.type`). A 2W courier is a
 * RIDER; a 4W truck driver is a DRIVER — the directory holds both, plus
 * warehouse staff and canvassers.
 */
export type TalentType = 'RIDER' | 'DRIVER' | 'WAREHOUSE' | 'CANVASSER';

export interface Driver {
  id: number;
  ID?: number;
  name: string;
  first_name: string;
  last_name: string;
  code?: string;
  nik?: string;
  status?: string;
  email?: string;
  phone?: string;
  phone_number?: string;
  phoneNumber?: string;
  mobile?: string;
  city?: {
    id?: number | string;
    name?: string;
  };
  partner?: { name?: string } | null;
  vehicle?: { type?: string } | null;
  tags?: Array<string | { id?: number | string; name: string }>;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface DriverListResponse {
  data: Driver[];
  pagination?: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export const getListDrivers = async (params: {
  active?: string;
  page?: string;
  size?: string;
  search?: string;
  sort?: string;
  order?: string;
  status?: string;
  handoverStates?: string;
  cityIds?: string;
}): Promise<DriverListResponse> => {
  try {
    const response = await Axios.get('/v3/drivers', { params });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Driver directory — `GET /v3/drivers` (nest-driver-service, paginated).
 * `search` matches name, MTR code, AND phone number (unlike the legacy
 * `/v1/drivers`, which only matches name + code). Requires `sort` + `order`
 * (defaulted here). Used by the Scheduled Instant rider pickers (O4).
 */
export const getListDriversV3 = async (params: {
  page?: string;
  size?: string;
  search?: string;
  sort?: string;
  order?: string;
  // Comma-separated tag names; server returns drivers having ALL of them
  // (e.g. 'Scheduled Instant' for the Dedicated Riders tab).
  tagNames?: string;
  // Comma-separated driver statuses; server returns drivers whose status is IN
  // this set (e.g. 'ACTIVE,READY_TO_WORK' to exclude SUSPEND/BLACKLISTED/etc).
  statuses?: string;
  // TalentType — RIDER (2W courier) | DRIVER (4W) | WAREHOUSE | CANVASSER.
  // Applied server-side as `type = ?` (nest-driver-service
  // driver.repository.ts:676), so 4W pickers can exclude everyone who is not
  // a truck driver.
  type?: TalentType;
}): Promise<DriverListResponse> => {
  try {
    const response = await Axios.get('/v3/drivers', {
      params: { sort: 'id', order: 'desc', ...params },
    });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * One driver from the directory — `GET /v3/drivers/:id` (nest-driver-service).
 * Carries the mitra's registered rekening (`driver_bank`): `bank.name`,
 * `bank_account_name`, `bank_account_number`. Only the fields the UJP wizard
 * reads are typed here; the real payload is much wider.
 */
export interface DriverDetail {
  id: number;
  code?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  bank?: { id: number; name: string } | null;
  bank_code?: string | null;
  bank_account_name?: string | null;
  bank_account_number?: string | null;
}

export const getDriverByIdV3 = async (id: string | number): Promise<DriverDetail> => {
  try {
    const response = await Axios.get(`/v3/drivers/${encodeURIComponent(String(id))}`);
    const body = response.data;
    return (body?.data ?? body) as DriverDetail;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** The rekening block the UJP wizard prefills from a picked driver, or null when the master has none. */
export const driverBankOf = (
  d: DriverDetail | null | undefined
): { bankName: string; accountNumber: string; accountHolder: string } | null => {
  if (!d) return null;
  const bankName = (d.bank?.name ?? d.bank_code ?? '').trim();
  const accountNumber = (d.bank_account_number ?? '').replace(/\D/g, '');
  const accountHolder = (d.bank_account_name ?? '').trim();
  if (!bankName && !accountNumber && !accountHolder) return null;
  return { bankName, accountNumber, accountHolder };
};
